/**
 * apps/web :: lib/rulediff-seed-api
 *
 * Typed wrapper over `GET /api/rule-diff/seed/{country}/{state}`
 * (Wave 17d PR-A backend). Always credentialed — the backend requires
 * a valid `rb_session` cookie and returns 401 otherwise.
 *
 * Wire shape mirrors the JSON files at `content/rule-diff/seed/{cc}-
 * {st}.json` (1,737-file corpus authored in Wave 17b/17c). Each file
 * is a 10-attribute array; the SDK preserves that array shape.
 *
 * This is the deterministic, instant alternative to
 * `lib/rulediff-api.ts::generateRuleDiff` (LLM-backed). Use this when:
 *   - The diff for a (country, state) pair is well-known and the
 *     reviewer-approved structured corpus has it. (Most common case.)
 *   - You want zero-latency, zero-LLM-cost lookup with severity-tagged
 *     reviewer notes.
 *
 * Use `generateRuleDiff` when you need free-text input or a
 * country/state pair outside the corpus.
 */
import { apiFetch } from "@/lib/api";

/** A single row in the seed JSON — one attribute for one (country, state) pair. */
export interface RuleDiffSeedEntry {
  country_code: string; // ISO 3166-1 alpha-2 uppercase, e.g. "MX", "GB"
  us_state_code: string; // 2-letter US-jurisdiction code (uppercase)
  attr_name: RuleDiffAttrName;
  /**
   * Per-attribute typed object. Most attributes use `{value: string}`
   * (enums); BAC uses `{value: number, unit: "%"}`. SDK keeps it loose
   * (record) so the rendering layer can branch on the attr_name.
   */
  value: { value: string | number; unit?: string };
  source_url: string;
  source_tier: number;
  retrieved_at: string; // ISO date e.g. "2026-04-26"
  /** 0.0–1.0; reviewer-assigned. Sub-0.85 is "FLAGGED FOR SME REVIEW". */
  confidence: number;
  /**
   * Bilateral context note. Always 1-3 sentences. The note text is
   * authoritative source for severity classification — see
   * {@link parseSeverity}.
   */
  reviewer_notes: string;
}

/** The 10 canonical attribute names. Locked schema across the corpus. */
export type RuleDiffAttrName =
  | "side_of_road"
  | "right_on_red_default"
  | "stop_sign_behavior"
  | "school_bus_stop_law"
  | "bac_standard_limit"
  | "seatbelt_law"
  | "motorcycle_helmet_law"
  | "mobile_phone_restriction"
  | "pedestrian_crosswalk_priority"
  | "move_over_law";

/** Display order across the page. Side-of-road first because it can be the dominant safety diff (LHS countries / VI). */
export const ATTR_DISPLAY_ORDER: readonly RuleDiffAttrName[] = [
  "side_of_road",
  "right_on_red_default",
  "stop_sign_behavior",
  "school_bus_stop_law",
  "bac_standard_limit",
  "seatbelt_law",
  "motorcycle_helmet_law",
  "mobile_phone_restriction",
  "pedestrian_crosswalk_priority",
  "move_over_law",
] as const;

/**
 * Severity classification parsed from `reviewer_notes`. Drives card
 * color treatment in the browse UI.
 *
 * Heuristic (case-insensitive substring on reviewer_notes):
 *   - "DOMINANT" or "DOMINANT DIFF" → critical (red border)
 *   - "SAFETY-CRITICAL" → critical (red border)
 *   - "SIGNIFICANT BEHAVIORAL DIFF" → warning (amber border)
 *   - "BEHAVIORAL DIFF" or "NOTABLE" → warning (amber border)
 *   - "RARE MATCH" or "matches CA" or "no diff" / "No diff" → match (green border)
 *   - default → neutral (no special border)
 */
export type RuleDiffSeverity = "critical" | "warning" | "match" | "neutral";

export function parseSeverity(reviewerNotes: string): RuleDiffSeverity {
  const upper = reviewerNotes.toUpperCase();
  if (upper.includes("DOMINANT") || upper.includes("SAFETY-CRITICAL")) {
    return "critical";
  }
  if (
    upper.includes("SIGNIFICANT BEHAVIORAL DIFF") ||
    upper.includes("BEHAVIORAL DIFF") ||
    upper.includes("NOTABLE DIFF") ||
    upper.includes("NOTABLE BEHAVIORAL DIFF")
  ) {
    return "warning";
  }
  if (
    upper.includes("RARE MATCH") ||
    upper.includes("NO DIFF FOR") ||
    upper.includes("SUBSTANCE MATCHES") ||
    upper.includes("RULE ITSELF MATCHES") ||
    upper.includes("NO MAJOR DIFF")
  ) {
    return "match";
  }
  return "neutral";
}

/**
 * Format an attribute value for display. Branches on attribute type;
 * BAC gets a percent suffix; enum values become space-separated Title
 * Case (e.g. `permitted_default` → "Permitted Default").
 *
 * Pure function — easy to unit-test in isolation; no React deps.
 */
export function formatAttrValue(entry: RuleDiffSeedEntry): string {
  const v = entry.value.value;
  if (typeof v === "number") {
    const unit = entry.value.unit ?? "";
    return `${v}${unit}`;
  }
  return String(v)
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Fetch the precomputed structured rule-diff for a (country, state)
 * pair. Returns the 10-entry array on 200; throws `ApiError` on any
 * non-2xx (caller handles 401 → /login redirect, 404 → "not yet
 * supported" UI).
 *
 * Both args accept upper- or lower-case 2-letter codes; the backend
 * normalizes to lowercase.
 */
export async function getSeedRuleDiff(
  country: string,
  state: string,
): Promise<RuleDiffSeedEntry[]> {
  const c = country.trim().toLowerCase();
  const s = state.trim().toLowerCase();
  return apiFetch<RuleDiffSeedEntry[]>(
    `/api/rule-diff/seed/${encodeURIComponent(c)}/${encodeURIComponent(s)}`,
    { method: "GET" },
  );
}
